import {
  AccountInfo,
  AuthenticationResult,
  InteractionRequiredAuthError,
  InteractionType,
  IPublicClientApplication,
} from "@azure/msal-browser";

import { loginRequest } from "../auth/auth-config.ts";

export interface IAccountWrapper {
  displayName: string;
  firstName: string;
  lastName: string;
  azureId: string;
  email: string;
  accessToken: string;
  idToken: string;
}
const logPair = (source: string, error: Error | null, result: AuthenticationResult | null) => {
  if (error) {
    console.error(`${source}:\n\terror-> ${error}\n\tresult-> ${result}`);
  }
};

const buildWrapper = (response: AuthenticationResult | null): IAccountWrapper => {
  const account = response?.account;
  if (!account) {
    throw new Error("No account info");
  }
  const nameParts = account.name?.split(" ");
  return {
    displayName: account.name ?? "N/A",
    firstName: nameParts?.slice(0, -1).join(" ") ?? "N/A",
    lastName: nameParts?.slice(-1).join(" ") ?? "N/A",
    azureId: account.localAccountId,
    email: account.username,
    accessToken: response?.accessToken ?? "",
    idToken: response?.idToken ?? "",
  };
};

async function acquireTokenSilent(
  pca: IPublicClientApplication,
  tokenRequest: any,
): Promise<[Error | null, AuthenticationResult | null]> {
  try {
    const result = await pca.acquireTokenSilent(tokenRequest);
    return [null, result];
  } catch (error: any) {
    if (!(error instanceof InteractionRequiredAuthError)) {
      return [error, null];
    }
    return [error, null];
  }
}

async function acquireTokenPopup(
  pca: IPublicClientApplication,
  tokenRequest: any,
): Promise<[Error | null, AuthenticationResult | null]> {
  try {
    const result = await pca.acquireTokenPopup(tokenRequest);
    return [null, result];
  } catch (error: any) {
    return [error, null];
  }
}

async function acquireTokenRedirect(
  pca: IPublicClientApplication,
  tokenRequest: any,
): Promise<[Error | null, AuthenticationResult | null]> {
  try {
    pca.acquireTokenRedirect(tokenRequest);
    const result = await pca.handleRedirectPromise();
    return [null, result];
  } catch (error: any) {
    return [error, null];
  }
}

async function authenticate(
  pca: IPublicClientApplication,
  account: AccountInfo,
  interactionType: InteractionType,
): Promise<[Error | null, AuthenticationResult | null]> {
  const tokenRequest = {
    ...loginRequest,
    account: account,
  };

  let [err, res] = await acquireTokenSilent(pca, tokenRequest);
  logPair("silent", err, res);
  if (!err) {
    return [err, res];
  }

  if (err instanceof InteractionRequiredAuthError) {
    switch (interactionType) {
      case InteractionType.Popup:
        [err, res] = await acquireTokenPopup(pca, tokenRequest);
        logPair("popup", err, res);
        break;
      case InteractionType.Redirect:
        [err, res] = await acquireTokenRedirect(pca, tokenRequest);
        logPair("redirect", err, res);
        break;
      default:
        [err, res] = [new Error("No interaction type"), null];
        logPair("default", err, res);
    }
  }

  if (err || res === null) {
    logPair("Error during authentication", err, res);
  }
  return [err, res];
}

function handleLogout(pca: IPublicClientApplication) {
  pca.logoutRedirect({
    postLogoutRedirectUri: "/",
  });
}

async function requestProfileData(
  pca: IPublicClientApplication,
  account: AccountInfo,
  interactionType: InteractionType,
): Promise<[Error | null, IAccountWrapper | null]> {
  const [authError, authResult] = await authenticate(pca, account, interactionType);
  return [authError, buildWrapper(authResult)];
}

const MsGraph = { requestProfileData, handleLogout };
export default MsGraph;
